import { useQuery }            from '@tanstack/react-query'
import { notificationService } from '@services'
import { useAuthStore }        from '@store'
import { QUERY_KEYS }          from '@constants/queryKeys'

/**
 * useNotificationsQuery — admin: all notifications (paginated + filtered).
 */
export function useNotificationsQuery(filters = {}) {
  return useQuery({
    queryKey:        QUERY_KEYS.notifications.list(filters),
    queryFn:         () => notificationService.getAll(filters),
    placeholderData: (prev) => prev,
  })
}

/**
 * useMyNotificationsQuery — portal: current user's notifications.
 */
export function useMyNotificationsQuery(filters = {}) {
  const user = useAuthStore((s) => s.user)
  const userId = user?.id ?? 'guest'

  return useQuery({
    queryKey: QUERY_KEYS.notifications.mine(userId),
    queryFn:  () => notificationService.getMine(userId, filters),
    refetchInterval: 30_000,  // poll every 30 s for new notifications
  })
}

/**
 * useNotificationQuery — single notification by id.
 */
export function useNotificationQuery(id) {
  return useQuery({
    queryKey: QUERY_KEYS.notifications.detail(id),
    queryFn:  () => notificationService.getById(id),
    enabled:  !!id,
  })
}

/**
 * useUnreadCountQuery — lightweight unread counter for the bell badge.
 */
export function useUnreadCountQuery() {
  const user = useAuthStore((s) => s.user)
  const userId = user?.id ?? 'guest'

  return useQuery({
    queryKey:        QUERY_KEYS.notifications.unread(userId),
    queryFn:         () => notificationService.getUnreadCount(userId),
    refetchInterval: 20_000,
    staleTime:       10_000,
  })
}

/**
 * useNotificationHistoryQuery — admin: full notification history.
 */
export function useNotificationHistoryQuery(filters = {}) {
  return useQuery({
    queryKey:        QUERY_KEYS.notificationHistory.list(filters),
    queryFn:         () => notificationService.getHistory(filters),
    placeholderData: (prev) => prev,
  })
}
